import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { operationsApi } from 'features/operations/operations.api';
import { Button } from 'shared/components/Button';
import { Badge, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { formatDateTime, formatUserLabel } from 'shared/utilities/format';

export function TicketDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.supportManage);
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [response, setResponse] = useState('');
  const [errors, setErrors] = useState({});
  const [conflict, setConflict] = useState(null);
  const [pendingAction, setPendingAction] = useState('');

  const query = useQuery({
    queryKey: queryKeys.supportTicket(id),
    queryFn: ({ signal }) => operationsApi.showSupportTicket(id, { signal }),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.supportTicket(id) });
    queryClient.invalidateQueries({ queryKey: ['operations', 'support-tickets'] });
  };

  const run = useMutation({
    mutationFn: ({ action, payload }) => {
      if (action === 'review') return operationsApi.reviewSupportTicket(id);
      if (action === 'resolve') return operationsApi.resolveSupportTicket(id, payload.admin_response);
      if (action === 'close') return operationsApi.closeSupportTicket(id);
      return operationsApi.reopenSupportTicket(id);
    },
    onSuccess: () => {
      setConflict(null);
      setErrors({});
      setResponse('');
      refresh();
      toast.success(t('status.saved'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) setErrors(mapFieldErrors(error.errors));
      if (error instanceof ApiError && error.isConflict()) {
        setConflict(error.message);
        return;
      }
      toast.error(humanizeApiError(error, t));
    },
    onSettled: () => setPendingAction(''),
  });

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  const ticket = query.data || {};
  const busy = run.isPending;

  return (
    <div className="stack-form">
      <PageHeader title={`${t('support.tickets')} ${ticket.reference || `#${ticket.id}`}`} />
      {conflict ? <p className="warning-banner" role="alert">{conflict} {t('conflict.keepLocal')}</p> : null}
      <p><strong>{t('consultations.status')}:</strong> <Badge>{t(`support.statuses.${ticket.status}`)}</Badge></p>
      <p><strong>{t('support.user')}:</strong> {formatUserLabel(ticket.user, t)}</p>
      <p><strong>{t('support.category')}:</strong> {locale === 'ar' ? ticket.category?.title_ar : ticket.category?.title_en}</p>
      <p><strong>{t('support.message')}:</strong> {ticket.message || '—'}</p>
      <p><strong>{t('consultations.createdFrom')}:</strong> {formatDateTime(ticket.created_at, locale)}</p>
      <p><strong>{t('support.adminResponse')}:</strong> {ticket.admin_response || '—'}</p>
      <p><strong>{t('support.resolvedAt')}:</strong> {formatDateTime(ticket.resolved_at, locale)}</p>
      <p className="muted">{t('support.noHistory')}</p>

      {canManage && ticket.status === 'open' ? (
        <Button
          loading={busy && pendingAction === 'review'}
          disabled={busy}
          onClick={() => {
            setPendingAction('review');
            run.mutate({ action: 'review', payload: {} });
          }}
        >
          {t('support.review')}
        </Button>
      ) : null}

      {canManage && ticket.status === 'in_review' ? (
        <form
          className="card stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            confirm({ body: t('support.resolve') }).then((ok) => {
              if (!ok) return;
              setPendingAction('resolve');
              run.mutate({ action: 'resolve', payload: { admin_response: response } });
            });
          }}
        >
          <label className="field" htmlFor="admin-response">
            <span className="field-label">{t('support.adminResponse')}</span>
            <textarea id="admin-response" className="input" value={response} onChange={(event) => setResponse(event.target.value)} required />
            {errors.admin_response?.[0] ? <span className="field-error">{errors.admin_response[0]}</span> : null}
          </label>
          <Button type="submit" loading={busy && pendingAction === 'resolve'} disabled={busy}>{t('support.resolve')}</Button>
        </form>
      ) : null}

      {canManage && ['in_review', 'resolved'].includes(ticket.status) ? (
        <Button
          tone="secondary"
          loading={busy && pendingAction === 'close'}
          disabled={busy}
          onClick={async () => {
            if (await confirm({ body: t('support.close') })) {
              setPendingAction('close');
              run.mutate({ action: 'close', payload: {} });
            }
          }}
        >
          {t('support.close')}
        </Button>
      ) : null}

      {canManage && ['resolved', 'closed'].includes(ticket.status) ? (
        <Button
          loading={busy && pendingAction === 'reopen'}
          disabled={busy}
          onClick={() => {
            setPendingAction('reopen');
            run.mutate({ action: 'reopen', payload: {} });
          }}
        >
          {t('support.reopen')}
        </Button>
      ) : null}
    </div>
  );
}
